import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/insurance.png";
import IndustryVerticalPage from "../../components/industry/industry-vertical-page";

const page = {
  title: "Insurance Management Software, Policy Administration & Claims Systems | Avani Cimcon",
  description:
    "Insurance solutions for policy administration, claims, agency portals, document management and COBOL modernization. Preserve legacy systems while adding enterprise AI enablement.",
  keywords:
    "insurance management software, insurance crm software, insurance, SOX compliant Document Management System, insurance policy management systems, Insurance Software, insurance policy system, insurance policy administration systems, administration systems, business product management, insurance management system, Online Quote Engine, automation, Commission Statements Automation, insurance agency / agent management systems, Producer Profile Management, Online Event Management, insurance management systems, insurance document management, insurance applications, web solutions for Insurance Company, Software for Insurance Industry, IT Solutions for Health Insurance, enterprise software engineering, legacy modernization, AI enablement, COBOL, claims management, policy administration",
  canonical: "https://www.avanicimcon.com/insurance",
  ogImage: "https://www.avanicimcon.com/images/banner/insurance.png",
  hero: {
    eyebrow: "Industry Expertise",
    heading: "Insurance",
    lead:
      "Insurance providers are seeking new and innovative methods to conduct business, but the operational reality still depends on policy administration systems, claims workflows, agency management and legacy application integration.",
    image: BannerImg,
    tags: ["Policy administration", "Claims systems", "COBOL integration", "Agency portals"],
    panelKicker: "Legacy systems. Modern enterprise delivery.",
    panelTitle: "Insurance platforms built for policy, claims and agency workflows.",
    panelText:
      "We help insurers and brokers preserve core systems while modernizing the surrounding digital experience, integration layer and workflow automation.",
    stats: [
      { value: "Policy Admin", label: "New business and policy management depth" },
      { value: "BRMS", label: "Business rules for regulated workflows" },
      { value: "COBOL", label: "Legacy integration and modernization experience" },
    ],
    primaryCta: { label: "Talk to Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
  },
  overview: {
    eyebrow: "Industry Overview",
    title: "Insurance workflows that combine underwriting, claims, portals and document control",
    lead:
      "The insurance industry depends on reliable systems for policy administration, agency management, document handling and e-commerce style self-service.",
    paragraphs: [
      "We have significant experience in the insurance domain and have worked with clients on agency solutions and e-commerce solutions for insurance. The original SEO terms are still relevant because they map to real buyer intent and long-lived authority around insurance software and insurance management systems.",
      "The modern framing is enterprise modernization: preserve the systems that run the business, integrate them with current applications, and layer AI where it increases operational value.",
    ],
    callout: {
      title: "What this page should prove",
      text: "Insurance buyers should see that Avani Cimcon understands regulated workflows, legacy COBOL environments and the systems that sit around policy administration.",
      items: [
        "Policy administration and new business processing",
        "Agency and agent management",
        "SOX-compliant document management",
      ],
    },
  },
  challenges: {
    eyebrow: "Industry Challenges",
    title: "Insurance technology has to balance regulation, legacy systems and rapid change",
    items: [
      {
        title: "Legacy COBOL environments",
        text: "Many insurers still depend on legacy core systems that cannot simply be replaced without business risk.",
      },
      {
        title: "Policy and claims complexity",
        text: "Policy administration, new business processing and claims handling require consistent business rules and auditability.",
      },
      {
        title: "Agency and customer portals",
        text: "Agents, brokers and customers need digital experiences that can sit on top of older enterprise systems.",
      },
    ],
  },
  systems: {
    eyebrow: "Enterprise Systems We Build & Modernize",
    title: "Insurance platforms that support policy, claims, agency and document operations",
    lead:
      "Keep the original terms because they still matter to search intent, then present them as part of a modernization story.",
    items: [
      {
        title: "Policy Administration",
        text: "Policy administration / new business processing systems that support regulated operations.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
      {
        title: "Licensing & Contracting",
        text: "Portal and workflow solutions for licensing, contracting and producer management.",
        href: "/it-consulting-services",
        linkLabel: "See solutions",
      },
      {
        title: "Document Management",
        text: "SOX-compliant document management systems for insurance documents and forms.",
        href: "/cms",
        linkLabel: "Explore CMS",
      },
      {
        title: "Business Rules Engine",
        text: "Rules-driven workflows for underwriting, adjudication and approvals.",
        href: "/biz-rule-engine",
        linkLabel: "Explore BRMS",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Keep the core insurance systems and modernize the integration layer",
    lead:
      "This page should make it clear that Avani Cimcon can work with COBOL, .NET and J2EE environments without forcing a risky replacement.",
    paragraphs: [
      "We successfully designed and implemented middleware that allows organizations to interface Internet-enabled applications with legacy systems written in COBOL. That is the exact type of modernization story the insurance vertical needs: preserve the investment, expose the legacy system safely and improve the experience around it.",
      "Interfacing .NET applications and J2EE applications with legacy COBOL applications is one of our specialties, allowing organizations to preserve expensive investments in legacy systems while still providing state-of-the-art e-enabled business front ends.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Preserve expensive legacy investments",
        "Expose systems through secure middleware",
        "Improve portal and self-service experiences",
        "Make business rules easier to maintain",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI should help insurance teams work faster inside existing systems",
    lead:
      "Introduce AI as a practical layer for underwriting, claims and service, not as a replacement for the insurance operating model.",
    items: [
      {
        title: "Underwriting support",
        text: "Use AI to support credit scoring, risk review, anomaly detection and submission triage.",
      },
      {
        title: "Claims assistance",
        text: "Use automation and AI to accelerate document handling, claim routing and exception management.",
      },
      {
        title: "Policy and service analytics",
        text: "Improve customer service and operational visibility with analytics layered onto existing policy systems.",
      },
    ],
  },
  experience: {
    eyebrow: "Our Experience in This Industry",
    title: "Insurance domain depth that spans systems, agency workflows and e-commerce",
    paragraphs: [
      "We have developed an array of solutions for the insurance industry, including policy administration, licensing and contracting, customized e-portals, illustrations software, CRM, business rules engine and SOX-compliant document management systems.",
      "That depth of experience gives us a practical understanding of how insurance organizations can keep legacy systems running while improving the workflows around them.",
    ],
    callout: {
      title: "Evidence to preserve",
      items: [
        "Agency solutions and e-commerce solutions",
        "Legacy COBOL integration",
        "Policy tracking and commission automation",
        "Document and forms engine history",
      ],
    },
  },
  relatedSolutions: [
    { label: "IT Consulting Services", href: "/it-consulting-services", text: "Modernization and delivery planning." },
    { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Policy and claims workflow automation." },
    { label: "DXchange", href: "/dxchange", text: "Enterprise data exchange and middleware." },
    { label: "Value Proposition", href: "/value-proposition", text: "Why enterprises modernize with Avani Cimcon." },
  ],
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "Insurance engineering built on enterprise delivery, not generic consulting language",
    lead:
      "The page should reinforce that this is an engineering and modernization partner with insurance-specific vocabulary and long-term delivery experience.",
    items: [
      {
        title: "Domain understanding",
        text: "We speak policy administration, claims, agency management and compliance in the same language insurers do.",
      },
      {
        title: "Legacy modernization",
        text: "We help insurers extend COBOL-backed systems instead of forcing unnecessary replacement.",
      },
      {
        title: "Authority preservation",
        text: "We preserve the original insurance keywords and technical depth that already have SEO value.",
      },
      {
        title: "Practical AI",
        text: "We introduce AI where it helps underwriting, claims and service without weakening the core story.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Modernize the insurance stack without losing the systems that matter",
    text:
      "Use enterprise software engineering, middleware and AI enablement to improve insurance workflows incrementally and safely.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class Insurance extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <IndustryVerticalPage {...page} />;
  }
}

export default Insurance;
